import React from 'react';
import { CheckCircle, X, Calendar, User, Hotel, DollarSign, Clock } from 'lucide-react';
import { format, differenceInDays, parseISO } from 'date-fns';

interface BookingDetails {
  userName: string;
  userEmail: string;
  roomName: string;
  checkIn: string;
  checkOut: string;
  totalPrice: number;
  nights: number;
}

interface BookingConfirmationModalProps {
  isOpen: boolean;
  onClose: () => void;
  bookingDetails: BookingDetails;
  onGoHome: () => void;
}

const BookingConfirmationModal: React.FC<BookingConfirmationModalProps> = ({
  isOpen,
  onClose,
  bookingDetails,
  onGoHome
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="px-6 py-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="flex-shrink-0 w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-3">
                <h3 className="text-lg font-medium text-gray-900">Booking Confirmed!</h3>
                <p className="text-sm text-gray-500">Your reservation IFA-{Math.random().toString(36).substr(2, 8).toUpperCase()} has been submitted successfully</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Booking Details */}
          <div className="bg-gray-50 rounded-lg p-4 mb-6">
            <h4 className="text-sm font-medium text-gray-900 mb-3">Booking Details</h4>
            
            <div className="space-y-3">
              <div className="flex items-center text-sm">
                <User className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                <span className="text-gray-600">Guest:</span>
                <span className="ml-1 font-medium text-gray-900">{bookingDetails.userName}</span>
              </div>
              
              <div className="flex items-center text-sm">
                <Hotel className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                <span className="text-gray-600">Room:</span>
                <span className="ml-1 font-medium text-gray-900">{bookingDetails.roomName}</span>
              </div>
              
              <div className="flex items-center text-sm">
                <Calendar className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                <span className="text-gray-600">Check-in:</span>
                <span className="ml-1 font-medium text-gray-900">
                  {format(parseISO(bookingDetails.checkIn), 'MMM d, yyyy')} at 12:00 PM
                </span>
              </div>
              
              <div className="flex items-center text-sm">
                <Calendar className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                <span className="text-gray-600">Check-out:</span>
                <span className="ml-1 font-medium text-gray-900">
                  {format(parseISO(bookingDetails.checkOut), 'MMM d, yyyy')} at 12:00 PM
                </span>
              </div>
              
              <div className="flex items-center text-sm">
                <Clock className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                <span className="text-gray-600">Duration:</span>
                <span className="ml-1 font-medium text-gray-900">
                  {bookingDetails.nights} night{bookingDetails.nights !== 1 ? 's' : ''}
                </span>
              </div>
              
              <div className="flex items-center text-sm pt-2 border-t border-gray-200">
                <DollarSign className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
                <span className="text-gray-600">Total Amount:</span>
                <span className="ml-1 font-bold text-lg text-indigo-600">
                  ${bookingDetails.totalPrice}
                </span>
              </div>
            </div>
          </div>

          {/* Confirmation Message */}
          

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={onGoHome}
              className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors duration-200"
            >
              Back to Home
            </button>
            <button
              onClick={onClose}
              className="flex-1 bg-gray-100 text-gray-700 px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-colors duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookingConfirmationModal;